import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle, AlertCircle, X, Info } from 'lucide-react';

const SimpleToast = ({ 
  isOpen, 
  onClose, 
  type = 'success', 
  title, 
  message, 
  showCloseButton = true,
  autoClose = false,
  autoCloseDelay = 3000,
  onConfirm = null,
  onCancel = null,
  handleConfirm = null,
  handleCancel = null
}) => {
  // Auto cerrar si está habilitado
  useEffect(() => {
    if (isOpen && autoClose) {
      const timer = setTimeout(() => {
        onClose();
      }, autoCloseDelay);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen, autoClose, autoCloseDelay, onClose]);

  if (!isOpen) return null;

  console.log('🍞 SimpleToast renderizando:', { isOpen, type, title, message });

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-6 h-6 text-green-600" />;
      case 'error':
        return <AlertCircle className="w-6 h-6 text-red-600" />;
      case 'warning':
        return <AlertCircle className="w-6 h-6 text-yellow-600" />;
      case 'info':
        return <Info className="w-6 h-6 text-blue-600" />;
      default:
        return <CheckCircle className="w-6 h-6 text-green-600" />;
    }
  };

  const getBgColor = () => {
    switch (type) {
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'info':
        return 'bg-blue-50 border-blue-200';
      default:
        return 'bg-green-50 border-green-200';
    }
  };

  const getTextColor = () => {
    switch (type) {
      case 'success':
        return 'text-green-800';
      case 'error':
        return 'text-red-800';
      case 'warning':
        return 'text-yellow-800';
      case 'info':
        return 'text-blue-800';
      default:
        return 'text-green-800';
    }
  };

  const toastContent = (
    <div 
      style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 99999999,
        maxWidth: '400px',
        minWidth: '300px',
        backgroundColor: 'white',
        border: '2px solid #007bff',
        borderRadius: '8px',
        padding: '16px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
        fontFamily: 'Arial, sans-serif'
      }}
    >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <div style={{ fontSize: '24px', marginTop: '2px' }}>
            {type === 'success' ? '✅' : type === 'error' ? '❌' : 'ℹ️'}
          </div>
          <div style={{ flex: 1 }}>
            {title && (
              <h3 style={{ 
                margin: '0 0 8px 0', 
                fontSize: '16px', 
                fontWeight: 'bold',
                color: type === 'success' ? '#155724' : type === 'error' ? '#721c24' : '#0c5460'
              }}>
                {title}
              </h3>
            )}
            {message && (
              <p style={{ 
                margin: '0', 
                fontSize: '14px',
                color: type === 'success' ? '#155724' : type === 'error' ? '#721c24' : '#0c5460'
              }}>
                {message}
              </p>
            )}
          </div>
          
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '18px',
              cursor: 'pointer',
              color: '#666',
              padding: '0',
              marginLeft: '8px'
            }}
          >
            ×
          </button>
        </div>
        
        {/* Botones de confirmación si están disponibles */}
        {(onConfirm || onCancel || handleConfirm || handleCancel) && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: '8px', 
            marginTop: '16px',
            paddingTop: '12px',
            borderTop: '1px solid #eee'
          }}>
            <button
              onClick={() => {
                if (handleCancel) handleCancel();
                else if (onCancel) onCancel();
                onClose();
              }}
              style={{
                padding: '8px 16px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                backgroundColor: '#f8f9fa',
                color: '#333',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Cancelar
            </button>
            <button
              onClick={() => {
                if (handleConfirm) handleConfirm();
                else if (onConfirm) onConfirm();
                onClose();
              }}
              style={{
                padding: '8px 16px',
                border: '1px solid #007bff',
                borderRadius: '4px',
                backgroundColor: '#007bff',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Aceptar
            </button>
          </div>
        )}
    </div>
  );

  // Renderizar en el body usando portal
  return createPortal(toastContent, document.body);
};

export default SimpleToast;
